import { useState } from 'react'
import { reservationApi } from '../services/api'
import AvailabilityCalendar from '../components/AvailabilityCalendar'

const START_TIMES = [
  '09:00', '10:00', '11:00', '12:00', '13:00', '14:00',
  '15:00', '16:00', '17:00', '18:00', '19:00', '20:00',
  '21:00', '22:00'
]

const DURATION_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8]

export default function Reserve() {
  const [selectedDate, setSelectedDate] = useState('')
  const [startTime, setStartTime] = useState('')
  const [duration, setDuration] = useState('')
  const [guestName, setGuestName] = useState('')
  const [guestEmail, setGuestEmail] = useState('')
  const [isGuest, setIsGuest] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState<any>(null)

  const ROOM_ID = process.env.REACT_APP_ROOM_ID || 'room-1'

  const getEndTime = () => {
    if (!startTime || !duration) return ''
    const [hours, minutes] = startTime.split(':').map(Number)
    const endHour = hours + Number(duration)
    if (endHour > 23) return ''
    return `${String(endHour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
  }

  const getAvailableDurations = () => {
    if (!startTime) return DURATION_OPTIONS
    const [hours] = startTime.split(':').map(Number)
    return DURATION_OPTIONS.filter(d => hours + d <= 23)
  }

  const endTime = getEndTime()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!selectedDate) {
      setError('日付を選択してください')
      return
    }

    if (!startTime || !duration) {
      setError('開始時間と利用時間を選択してください')
      return
    }

    if (!endTime) {
      setError('終了時間が23時を超えるため予約できません')
      return
    }

    setLoading(true)
    try {
      const startAt = `${selectedDate}T${startTime}:00+09:00`
      const endAt = `${selectedDate}T${endTime}:00+09:00`
      const data: any = { roomId: ROOM_ID, startAt, endAt }
      if (isGuest) {
        data.guestName = guestName
        data.guestEmail = guestEmail
      }
      const response = await reservationApi.create(data)
      setDone(response.data)
    } catch (err: any) {
      setError(err.response?.data?.error || '予約に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  if (done) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.title}>予約完了</h1>
          <p style={styles.success}>ご予約が確定しました！</p>
          <div style={styles.pinBox}>
            <p style={styles.pinLabel}>入室用PINコード</p>
            <p style={styles.pin}>{done.pin}</p>
            <p style={styles.pinNote}>※予約開始15分前から有効です</p>
          </div>
          <p>確認メールをお送りしました。</p>
          <button onClick={() => setDone(null)} style={styles.button}>
            新しい予約をする
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>レンタルルーム予約</h1>

        <AvailabilityCalendar
          onSelectDate={setSelectedDate}
          selectedDate={selectedDate}
        />

        {error && <p style={styles.error}>{error}</p>}
        <form onSubmit={handleSubmit}>
          <div style={styles.field}>
            <label style={styles.label}>選択中の日付</label>
            <input
              type="text"
              value={selectedDate || '日付を選択してください'}
              style={{ ...styles.input, backgroundColor: '#f5f5f5' }}
              readOnly
            />
          </div>
          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>開始時間</label>
              <select
                value={startTime}
                onChange={(e) => { setStartTime(e.target.value); setDuration('') }}
                style={styles.input}
                required
              >
                <option value="">選択してください</option>
                {START_TIMES.map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>利用時間</label>
              <select
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                style={styles.input}
                required
                disabled={!startTime}
              >
                <option value="">選択してください</option>
                {getAvailableDurations().map(d => (
                  <option key={d} value={d}>{d}時間</option>
                ))}
              </select>
            </div>
          </div>
          {endTime && (
            <div style={styles.timeConfirm}>
              <span>利用時間：{startTime} 〜 {endTime}</span>
            </div>
          )}
          <div style={styles.field}>
            <label style={styles.label}>予約種別</label>
            <div>
              <label style={{ marginRight: '16px' }}>
                <input
                  type="radio"
                  checked={isGuest}
                  onChange={() => setIsGuest(true)}
                /> ゲスト（会員登録不要）
              </label>
              <label>
                <input
                  type="radio"
                  checked={!isGuest}
                  onChange={() => setIsGuest(false)}
                /> 会員
              </label>
            </div>
          </div>
          {isGuest && (
            <>
              <div style={styles.field}>
                <label style={styles.label}>お名前</label>
                <input
                  type="text"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  style={styles.input}
                  required
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>メールアドレス</label>
                <input
                  type="email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  style={styles.input}
                  required
                />
              </div>
            </>
          )}
          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? '予約中...' : '予約を確定する'}
          </button>
        </form>
        <p style={styles.link}>
          会員の方は<a href="/login">ログイン</a> ・ <a href="/register">新規登録</a>
        </p>
      </div>
    </div>
  )
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    padding: '20px'
  },
  card: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    width: '100%',
    maxWidth: '600px'
  },
  title: {
    textAlign: 'center',
    marginBottom: '24px',
    color: '#333'
  },
  field: {
    marginBottom: '16px',
    flex: 1
  },
  row: {
    display: 'flex',
    gap: '16px'
  },
  label: {
    display: 'block',
    marginBottom: '4px',
    color: '#555',
    fontSize: '14px'
  },
  input: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '16px',
    boxSizing: 'border-box'
  },
  timeConfirm: {
    backgroundColor: '#f0f7ff',
    border: '1px solid #1F4E79',
    borderRadius: '4px',
    padding: '10px 16px',
    marginBottom: '16px',
    color: '#1F4E79',
    fontWeight: 'bold',
    fontSize: '14px'
  },
  button: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#1F4E79',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    cursor: 'pointer',
    marginTop: '8px'
  },
  error: {
    color: 'red',
    marginBottom: '16px',
    fontSize: '14px'
  },
  success: {
    color: 'green',
    textAlign: 'center',
    fontSize: '16px'
  },
  pinBox: {
    backgroundColor: '#f0f7ff',
    border: '2px solid #1F4E79',
    borderRadius: '8px',
    padding: '20px',
    textAlign: 'center',
    margin: '20px 0'
  },
  pinLabel: {
    color: '#1F4E79',
    fontWeight: 'bold',
    marginBottom: '8px'
  },
  pin: {
    fontSize: '36px',
    fontWeight: 'bold',
    letterSpacing: '8px',
    color: '#1F4E79'
  },
  pinNote: {
    fontSize: '12px',
    color: '#888',
    marginTop: '8px'
  },
  link: {
    textAlign: 'center',
    marginTop: '16px',
    fontSize: '14px',
    color: '#555'
  }
}
